import { Translate } from '@chipinside/translate'
import { Box, Stack, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { CameraOff } from 'lucide-react'
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react'

import { ImageAction } from '#/components/FormCall/Inputs/InputImage/types'
import { Actions } from '#/components/FormCall/Inputs/InputImage/View/actions'
import { ImageActionKey, ImageProps } from '#/components/Image/types'
import { CircularLoader } from '#/components/Loader/Circular'
import { download, fileToURL, urlToFile } from '#/utils/download'
import { exitFullscreen, requestFullscreen } from '#/utils/fullscreen'
import { mergeSx } from '#/utils/mergeSx'

const Picture = styled('img', {
  shouldForwardProp: prop => prop !== 'fullscreen',
})<{ fullscreen?: boolean }>(({ fullscreen }) => ({
  borderRadius: 16,
  display: 'block',
  maxHeight: '75vh',
  maxWidth: '100%',
  ...(fullscreen && {
    borderRadius: 0,
    height: '100%',
    maxHeight: 'none !important',
    maxWidth: 'none !important',
    objectFit: 'contain',
    width: '100%',
  }),
}))

const resolveImageActions = (fullscreen: boolean) => {
  const actions: ImageAction<ImageActionKey>[] = [
    { color: 'primary', enabled: true, icon: 'download', key: 'download' },
    { color: 'primary', enabled: !fullscreen, icon: 'maximize', key: 'fullscreen' },
    { color: 'primary', enabled: fullscreen, icon: 'minimize', key: 'exit_fullscreen' },
  ]

  return actions.filter(({ enabled }) => enabled)
}

export const Image = forwardRef<HTMLImageElement, ImageProps>(function Image(
  {
    actions = true,
    alt = '',
    failed,
    file,
    onFullscreenChange,
    onLoadFile,
    slotProps = {},
    url,
    ...rest
  },
  ref,
) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [fullscreen, setFullscreen] = useState(false)
  const [downloaded, setDownloaded] = useState(file)
  const [loadFailed, setLoadFailed] = useState(false)
  const src = useMemo(() => (downloaded ? fileToURL(downloaded) : undefined), [downloaded])

  useEffect(() => {
    if (file) return setDownloaded(file)

    loadFile()
  }, [file, url])

  useEffect(() => {
    if (!src) return

    return () => URL.revokeObjectURL(src)
  }, [src])

  const loadFile = async () => {
    if (!url) return

    setLoadFailed(false)

    const loadedFile = await urlToFile({ url })

    if (!loadedFile) return setLoadFailed(true)

    setDownloaded(loadedFile)
    onLoadFile?.(loadedFile)
  }

  const changeFullscreen = (next: boolean) => {
    setFullscreen(next)
    onFullscreenChange?.(next)
  }

  useEffect(() => {
    const syncFullscreen = () => {
      if (!document.fullscreenElement) changeFullscreen(false)
    }

    document.addEventListener('fullscreenchange', syncFullscreen)

    return () => document.removeEventListener('fullscreenchange', syncFullscreen)
  }, [])

  const handleDownload = () => {
    if (!src || !downloaded) return

    download({ fileName: downloaded.name, fileURL: src })
  }

  const toggleFullscreen = (next: boolean) => {
    const container = containerRef.current

    if (!container) return

    if (next) {
      requestFullscreen(container)
    } else {
      exitFullscreen(container)
    }

    changeFullscreen(next)
  }

  const handleAction = (key: ImageActionKey) => {
    switch (key) {
      case 'download': {
        handleDownload()
        break
      }
      case 'exit_fullscreen': {
        toggleFullscreen(false)
        break
      }
      case 'fullscreen': {
        toggleFullscreen(true)
        break
      }
    }
  }

  if (failed || loadFailed) return <Failed slotProps={slotProps} />

  if (!src) return <CircularLoader sx={slotProps.loader?.sx} />

  return (
    <Box
      ref={containerRef}
      sx={mergeSx(
        {
          alignItems: 'center',
          display: 'flex',
          height: fullscreen ? '100%' : undefined,
          justifyContent: 'center',
          position: 'relative',
          width: fullscreen ? '100%' : undefined,
        },
        slotProps.root?.sx,
      )}
    >
      <Picture
        {...rest}
        alt={alt}
        fullscreen={fullscreen}
        onError={() => setLoadFailed(true)}
        ref={ref}
        src={src}
        sx={mergeSx(slotProps.picture?.sx)}
      />
      {actions && (
        <Actions
          actions={resolveImageActions(fullscreen)}
          onAction={handleAction as any}
          position="bottom-right"
          sx={slotProps.actions?.sx}
        />
      )}
    </Box>
  )
})

const Failed = ({ slotProps = {} }: { slotProps: ImageProps['slotProps'] }) => (
  <Stack
    sx={mergeSx(
      {
        alignItems: 'center',
        alignSelf: 'center',
        justifyContent: 'center',
        minHeight: 300,
        opacity: 0.4,
      },
      slotProps.failed?.sx,
    )}
  >
    <CameraOff size={48} strokeWidth={1.5} />
    <Typography sx={{ fontWeight: 500 }}>
      {Translate({ messageKey: 'image_load_error' })}
    </Typography>
  </Stack>
)
